import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { format } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { AddProfileImage } from '@components/add-profile-image';
import { Collapse } from '@components/animators/collapse';
import { BasicFade } from '@components/animators/fade';
import { Slide } from '@components/animators/slide';
import { Avatar } from '@components/avatar';
import { AvatarInfo } from '@components/avatar-info';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import {
  FormValidatorType,
  createForm,
  useCreateFormStore,
  useFormContext,
} from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropside } from '@components/icons/dropside';
import { IconEdit } from '@components/icons/edit';
import { IconFilter } from '@components/icons/filter';
import { IconSearch } from '@components/icons/search';
import { IconTravellerCreate } from '@components/icons/traveller-create';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { NoResultCard } from '@components/no-result-card';
import { SearchBox } from '@components/search-box';
import { SelectOption } from '@components/selectmulti';
import {
  CloseButton,
  Sidebar,
  hideSidebar,
  setSidebarVariant,
  useSidebar,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { hasNumericValue } from '@utils/numbers';
import { getKeys } from '@utils/objects';
import { toQueryString } from '@utils/requests';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import {
  useCompanyTravellersPaginated,
  useCreateCompanyTraveller,
} from 'api/company-traveller/hooks';
import { CompanyTraveller } from 'api/company-traveller/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import { useList } from 'api/lists/hooks';
import {
  useCreateJourneyTraveller,
  useEditJourneyTraveller,
  useJourneyTraveller,
} from 'api/traveller-journeys/hooks';
import { TravellerJourneyDetails } from 'api/traveller-journeys/types';
import { TravellerAttribute, TravellerJourney } from 'api/types';
import { useActiveUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';
import { routePaths } from 'ui/authenticated/navigation';

import {
  companyTravellerAttributePrefix,
  getMultiOptionValuesArray,
  journeyAttributePrefix,
  useCurrentWorkspace,
} from '../../helpers';
import { CompanyTravellerAttributeFields } from '../company-travellers/sidebars/form';
import { ViewCompanyTravellerAttributes } from '../company-travellers/sidebars/profile';

export const useCompanyTravellerToJourneySidebar = () => {
  const sidebar = useSidebar(CompanyTravellerToJourney, {
    variant: 'REGULAR',
  });
  return sidebar;
};
export type ShowCompanyTravellerToJourneySidebar = ReturnType<
  typeof useCompanyTravellerToJourneySidebar
>;

export const CompanyTravellerToJourney = ({
  journeyShortId,
  journeyTravellerId,
}: {
  journeyShortId: string;
  journeyTravellerId?: string;
}) => {
  const { t } = useScopedTranslate(AddCompanyTravellerSidebarLocale);
  const { showSuccess } = useToaster();
  const [companyTraveller, setCompanyTraveller] = useState<CompanyTraveller | undefined>(undefined);
  const [searchStr, setSearchStr] = useState('');
  const [shouldShowAdded, setShouldShowAdded] = useState(false);
  const [isCreateTraveller, setIsCreateTraveller] = useState<boolean>(false);
  const journeyTravellerQuery = useJourneyTraveller(journeyTravellerId);
  const { workspaceId } = useCurrentWorkspace();
  const journeyTraveller = journeyTravellerQuery?.data;
  const userQuery = useActiveUsers();
  const journeyAttributeTemplatesQuery = useJourneyAttributes(journeyShortId);
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const companyTravellerAttributeTemplates = companyTravellerAttributeTemplatesQuery.data ?? [];
  const onBack = () => {
    companyTraveller ? setCompanyTraveller(undefined) : setIsCreateTraveller(false);
  };
  const onCreate = (data: { id: string }) => {
    onBack();
    showSuccess(t('travellerAddedToJourney'), {
      url: generatePath(routePaths.traveller, {
        workspaceId: workspaceId,
        journeyId: journeyShortId,
        travellerId: data.id,
        '*': null,
      }),
    });
  };
  const journeyAttributes = journeyAttributeTemplatesQuery.data ?? [];

  const createJourneyTravellerMutation = useCreateJourneyTraveller(journeyShortId, {
    onSuccess: (data: { id: string }) => onCreate(data),
  });

  const queryString = useMemo(() => {
    return toQueryString({
      exclude_journey: shouldShowAdded ? undefined : journeyShortId,
      ordering: 'name',
      page_size: 25,
      page: 1,
      ...(searchStr && { search: searchStr }),
    });
  }, [searchStr, shouldShowAdded, journeyShortId]);

  const companyTravellersQuery = useCompanyTravellersPaginated(queryString);

  const companyTravellers = companyTravellersQuery.data?.results ?? [];
  const companyTravellersEmails = companyTravellers.map((traveller) => traveller.email);
  const isEdit = journeyTraveller !== undefined;

  const showCreateJourneyTraveller = companyTraveller || journeyTraveller;
  const showCreateCompanyTraveller = isCreateTraveller && !showCreateJourneyTraveller;
  const showSearch = !isEdit && !isCreateTraveller && !showCreateJourneyTraveller;
  useEffect(() => {
    setSidebarVariant(
      showCreateCompanyTraveller || showCreateJourneyTraveller ? 'CONTENT_HEAVY' : 'REGULAR'
    );
  }, [showCreateCompanyTraveller, showCreateJourneyTraveller]);

  if (companyTravellersQuery.isLoading || (journeyTravellerId && journeyTravellerQuery.isLoading)) {
    return <Sidebar.Spinner />;
  }

  if (
    companyTravellersQuery.isLoading ||
    companyTravellerAttributeTemplatesQuery.isLoading ||
    userQuery.isLoading
  )
    return <Spinner />;
  const sidebarTitle = isEdit
    ? t('editTravellerJourney')
    : isCreateTraveller
    ? t('createTraveller')
    : t('addToJourney');

  const hasActiveJourneyAttributes = journeyAttributes.some((attribute) => attribute.isActive);

  const onAdd = (traveller: CompanyTraveller) => {
    if (hasActiveJourneyAttributes) {
      setCompanyTraveller(traveller);
    } else {
      const formData = new FormData();
      formData.append('journey', journeyShortId);
      formData.append('companyTraveller', traveller.shortId);
      createJourneyTravellerMutation.mutateAsync(formData);
    }
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              {isEdit ? (
                <IconEdit color="elementBlue100" />
              ) : (
                <IconAddPlus color="elementBlue100" />
              )}
              <Sidebar.HeaderTitle title={sidebarTitle} />
            </Stack>
            <Stack orientation="horizontal">
              {!isEdit && (
                <AnimatePresence initial={false}>
                  {!showSearch ? (
                    <BasicFade>
                      <Button
                        variant="ghost"
                        text="Back"
                        iconLeft={<IconArrowLeft />}
                        onClick={onBack}
                      />
                    </BasicFade>
                  ) : (
                    <IconButton
                      icon={<IconTravellerCreate />}
                      variant="ghost-purple"
                      onClick={() => setIsCreateTraveller(true)}
                    />
                  )}
                </AnimatePresence>
              )}
              <CloseButton />
            </Stack>
          </Stack>
          {showSearch && (
            <SearchContainer>
              <Menu
                trigger={IconButton}
                triggerProps={{ icon: <IconFilter /> }}
                placement="bottom-start"
              >
                <Menu.Item
                  onSelect={() => setShouldShowAdded(!shouldShowAdded)}
                  selectionType="open"
                  label={t('showAlreadyAdded')}
                  iconRight={
                    <Switch label="" onChange={setShouldShowAdded} value={shouldShowAdded} />
                  }
                ></Menu.Item>
              </Menu>
              <SearchBox
                onChange={setSearchStr}
                onChangeDebounce={500}
                placeholder={t('search')}
                defaultValue={searchStr}
                autoFocus
              />
            </SearchContainer>
          )}
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          {showCreateJourneyTraveller ? (
            <Slide key="create-journey-traveller" offset={30}>
              {journeyTraveller && (
                <CreateJourneyTraveller
                  traveller={journeyTraveller}
                  journeyShortId={journeyShortId}
                  companyTravellerAttributeTemplates={companyTravellerAttributeTemplates}
                  usersData={userQuery?.data}
                  createJourneyTravellerMutation={createJourneyTravellerMutation}
                  journeyAttributes={journeyAttributes}
                />
              )}
              {companyTraveller && (
                <CreateJourneyTraveller
                  traveller={companyTraveller}
                  journeyShortId={journeyShortId}
                  companyTravellerAttributeTemplates={companyTravellerAttributeTemplates}
                  usersData={userQuery?.data}
                  createJourneyTravellerMutation={createJourneyTravellerMutation}
                  journeyAttributes={journeyAttributes}
                />
              )}
            </Slide>
          ) : (
            <AnimatePresence initial={false} exitBeforeEnter>
              {showCreateCompanyTraveller ? (
                <Slide key="create-company-traveller" offset={30}>
                  <CreateCompanyTraveller
                    journeyShortId={journeyShortId}
                    setCompanyTraveller={setCompanyTraveller}
                    setIsCreateTraveller={setIsCreateTraveller}
                    companyTravellerAttributeTemplates={companyTravellerAttributeTemplates}
                    companyTravellersEmails={companyTravellersEmails}
                    hasJourneyAttributes={hasActiveJourneyAttributes}
                    createJourneyTravellerMutation={createJourneyTravellerMutation}
                  />
                </Slide>
              ) : (
                <Slide key="search" offset={-30}>
                  <Stack space={4}>
                    {companyTravellers.length === 0 && (
                      <EmptyState
                        skipMargin
                        content={t(searchStr ? 'noResults' : 'emptyText')}
                        icon={searchStr ? <IconSearch size={40} /> : <IconCheckmark />}
                        iconShadow={
                          searchStr ? <IconSearch size={110} /> : <IconCheckmark size={110} />
                        }
                        variant="small"
                        action={
                          <Stack space={2} orientation="horizontal" justify="left">
                            <Button
                              iconLeft={<IconAddPlus />}
                              text={t('createTraveller')}
                              variant="primary"
                              onClick={() => setIsCreateTraveller(true)}
                            />
                          </Stack>
                        }
                      />
                    )}
                    <Stack space={2}>
                      {companyTravellers.map((companyTraveller) => (
                        <CompanyTravellerCard
                          key={companyTraveller.id}
                          companyTraveller={companyTraveller}
                          onAdd={onAdd}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Slide>
              )}
            </AnimatePresence>
          )}
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

const SearchContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
})({
  '.search-box-container': {
    flex: 1,
  },
});

export type UserCardProps = {
  companyTraveller: CompanyTraveller;
  onAdd: (traveller: CompanyTraveller) => void;
};

export const CompanyTravellerCard = ({ companyTraveller, onAdd }: UserCardProps) => {
  const { t } = useScopedTranslate(AddCompanyTravellerSidebarLocale);
  return (
    <Card spaceX={4} spaceY={2} interactive>
      <Stack orientation="horizontal" align="center" justify="space-between">
        <Stack orientation="horizontal" align="center" space={4}>
          <Avatar name={companyTraveller.name} src={companyTraveller.picture} size="md" />
          <div>
            <Text font="semibold12" text={companyTraveller.name} />
            <Text font="regular12" text={companyTraveller.email} color="textSecondary" />
            <Text
              font="regular12"
              text={companyTraveller.phone ?? t('nophone')}
              color={companyTraveller.phone ? 'textSecondary' : 'textHelper'}
            />
          </div>
        </Stack>
        <Stack space={4} orientation="horizontal" align="center">
          <Button
            iconLeft={<IconAddPlus />}
            text={t('add')}
            variant="ghost"
            onClick={() => onAdd(companyTraveller)}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

type TravellerFormBaseFields = {
  name: string;
  email: string;
  phone: string;
};

type TravellerJourneyAttributes = {
  [K in `journey_attribute__${string}`]: string | File | File[] | Date | SelectOption[];
};

type CompanyTravellerAttributes = {
  [K in `company_traveller_attribute__${string}`]: string;
};

type Attributes = TravellerJourneyAttributes & CompanyTravellerAttributes;

type TravellerFormData = TravellerFormBaseFields & Attributes & { startJourney: boolean };

const Form = createForm<TravellerFormData>();

type CreateCompanyTravellerProps = {
  journeyShortId: string;
  setCompanyTraveller: (traveller: CompanyTraveller) => void;
  setIsCreateTraveller: (value: boolean) => void;
  companyTravellerAttributeTemplates: TravellerAttributeTemplate[];
  companyTravellersEmails: string[];
  hasJourneyAttributes: boolean;
  createJourneyTravellerMutation: ReturnType<typeof useCreateJourneyTraveller>;
  journeyTraveller?: TravellerJourney;
};

const CreateCompanyTraveller = (props: CreateCompanyTravellerProps) => {
  const {
    journeyShortId,
    setCompanyTraveller,
    setIsCreateTraveller,
    companyTravellerAttributeTemplates,
    companyTravellersEmails,
    hasJourneyAttributes,
    createJourneyTravellerMutation,
  } = props;
  const { t } = useScopedTranslate(AddCompanyTravellerSidebarLocale);
  const formStore = useCreateFormStore();
  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);

  const onCreateSuccess = (traveller: CompanyTraveller) => {
    setIsCreateTraveller(false);
    setCompanyTraveller(traveller);
  };

  const createMutation = useCreateCompanyTraveller(onCreateSuccess);

  const email = formStore.useFieldValue('email') as string;
  const formErrors: { [key: string]: string } = {};

  const checkEmail = () => {
    if (companyTravellersEmails.includes(email)) {
      formErrors['email'] = t('travellerExists');
    }
    formStore.actions.setErrors(formErrors);
  };

  const handleSave = (data: TravellerFormData) => {
    if (Object.keys(formErrors).length > 0) {
      return formStore.actions.setErrors(formErrors);
    }
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    if (data.phone) formData.append('phone', data.phone);

    const keys = getKeys(data);

    if (companyTravellerAttributeTemplates) {
      if (!data) return;

      for (const key of keys) {
        if (key.startsWith(companyTravellerAttributePrefix)) {
          const value = data[key] ?? '';
          const attributeKey = key.replace(companyTravellerAttributePrefix, '');
          const attribute = companyTravellerAttributeTemplates.find(
            (attr) => attr.shortId === attributeKey
          );
          if (attribute?.attributeType === 'date' && value instanceof Date) {
            formData.append(key, value ? format(new Date(value), 'yyyy-MM-dd') : '');
          } else if (attribute?.attributeType === 'multi_select') {
            if (!Array.isArray(value)) {
              continue;
            }
            const arrayOfValues = getMultiOptionValuesArray(value);
            formData.append(key, JSON.stringify(arrayOfValues));
          } else if (attribute?.attributeType === 'file') {
            if (value instanceof File) {
              formData.append(key, value);
            } else if (!value) {
              formData.append(`${key}__remove`, '');
            }
          } else if (attribute?.attributeType === 'file_multi') {
            if (Array.isArray(value)) {
              value.forEach((file, index) => {
                if (file instanceof File) {
                  formData.append(`${key}__${index}`, file);
                }
              });
            }
          } else {
            formData.append(key, value as string);
          }
        }
      }
    }
    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    if (hasJourneyAttributes) {
      return createMutation.mutateAsync(formData);
    } else {
      formData.append('journey', journeyShortId);
      createJourneyTravellerMutation.mutateAsync(formData);
    }
  };

  return (
    <Form store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <Grid>
          <div>
            <Text font="semibold14" text={t('details')} />
          </div>
          <Stack space={4}>
            <Stack space={4} orientation="horizontal" align="center">
              <AddProfileImage
                picture={picture}
                setPicture={setPicture}
                clearPicture={clearPicture}
                setClearPicture={setClearPicture}
              />
              <div>
                <Text font="semibold14" text={t('picture')} />
                <Text font="regular14" text={t('acceptedFiles')} color="textHelper" />
              </div>
            </Stack>
            <Form.TextField id="name" label={t('core.fullName')} validators={['required']} />
            <Form.TextField
              id="email"
              label={t('core.email')}
              validators={['required', 'email']}
              onBlur={checkEmail}
            />
            <Form.PhoneField id="phone" label={t('core.phone')} validators={['phone']} />
          </Stack>
        </Grid>
        <Sidebar.Separator />
        <Grid>
          <div>
            <Text font="semibold14" text={t('profileFields')} />
          </div>
          <Stack space={4}>
            {companyTravellerAttributeTemplates?.map(
              (attribute) =>
                attribute.isActive && (
                  <CompanyTravellerAttributeFields
                    key={attribute.shortId}
                    attributeTemplate={attribute}
                  />
                )
            )}
          </Stack>
        </Grid>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
          <Form.Submit
            text={hasJourneyAttributes ? t('createTraveller') : t('createAndAdd')}
            variant="primary-blue"
          />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

type CreateJourneyTravellerProps = {
  traveller: TravellerJourneyDetails | CompanyTraveller;
  journeyShortId: string;
  createJourneyTravellerMutation: ReturnType<typeof useCreateJourneyTraveller>;
  journeyAttributes: AttributeType[];
  companyTravellerAttributeTemplates?: TravellerAttributeTemplate[];
  usersData?: User[];
};

const CreateJourneyTraveller = (props: CreateJourneyTravellerProps) => {
  const {
    traveller,
    companyTravellerAttributeTemplates,
    journeyShortId,
    usersData,
    createJourneyTravellerMutation,
    journeyAttributes,
  } = props;
  const { t } = useScopedTranslate(AddCompanyTravellerSidebarLocale);
  const editJourneyTravellerMutation = useEditJourneyTraveller(traveller.shortId, journeyShortId, {
    onSuccess: hideSidebar,
  });
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const startJourney = formValues['startJourney'] as boolean;
  const isEdit = 'journeyAttributes' in traveller;
  const [showTravellerAttributes, setShowTravellerAttributes] = useState(true);
  const [showJourneyAttributes, setShowJourneyAttributes] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const filteredCoreAttributes = companyTravellerAttributeTemplates?.filter(
    (coreAttribute) => coreAttribute.isActive
  );

  const onFileMultiRemove = (file: string) => {
    setFilesToRemove([...filesToRemove, file]);
  };

  const handleSave = (data: TravellerJourneyAttributes) => {
    const formData = new FormData();
    formData.append('journey', journeyShortId);
    if (!isEdit) {
      formData.append('companyTraveller', traveller.shortId);
      formData.append('startJourney', startJourney.toString());
    }
    const keys = getKeys(data);

    if (journeyAttributes.length > 0) {
      if (!data) return;
      for (const key of keys) {
        if (key.startsWith(journeyAttributePrefix)) {
          const value = data[key] ?? '';
          const attributeKey = key.replace(journeyAttributePrefix, '');
          const attribute = journeyAttributes.find((attr) => attr.shortId === attributeKey);
          if (attribute?.attributeType === 'date' && value instanceof Date) {
            formData.append(key, value ? format(new Date(value), 'yyyy-MM-dd') : '');
          } else if (attribute?.attributeType === 'multi_select') {
            if (!Array.isArray(value)) {
              continue;
            }
            const arrayOfValues = getMultiOptionValuesArray(value);
            formData.append(key, JSON.stringify(arrayOfValues));
          } else if (attribute?.attributeType === 'file') {
            if (value instanceof File) {
              formData.append(key, value);
            } else if (!value) {
              formData.append(`${key}__remove`, '');
            }
          } else if (attribute?.attributeType === 'file_multi') {
            if (Array.isArray(value)) {
              value.forEach((file, index) => {
                if (file instanceof File) {
                  formData.append(`${key}__${index}`, file);
                }
              });
            }
            for (const fileShortId of filesToRemove) {
              formData.append(`${key}__remove__${fileShortId}`, fileShortId);
            }
          } else {
            formData.append(key, value as string);
          }
        }
      }
    }
    if (isEdit) {
      return editJourneyTravellerMutation.mutateAsync(formData);
    } else {
      return createJourneyTravellerMutation.mutateAsync(formData);
    }
  };

  return (
    <Form store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <Stack space={4} orientation="horizontal" align="center">
          <AvatarInfo
            title={traveller.name}
            subtitles={[traveller.email, traveller.phone]}
            src={traveller.picture}
          />
        </Stack>
        <Sidebar.Separator />
        <Grid>
          <div>
            <Button
              variant="ghost"
              text={t('travellerAttributes')}
              iconRight={showTravellerAttributes ? <IconDropside /> : <IconDropdown />}
              onClick={() => setShowTravellerAttributes(!showTravellerAttributes)}
            />
          </div>
          <Collapse isCollapsed={showTravellerAttributes}>
            <Stack space={4}>
              <div>
                <Text text={t('core.fullName')} font="semibold14" />
                <Text font="regular14" text={traveller.name} />
              </div>
              <div>
                <Text text={t('core.email')} font="semibold14" />
                <Text font="regular14" text={traveller.email} />
              </div>
              <div>
                <Text text={t('core.phone')} font="semibold14" />
                <Text
                  font="regular14"
                  text={Boolean(traveller.phone) ? traveller.phone : t('nophone')}
                  color={Boolean(traveller.phone) ? 'textPrimary' : 'textHelper'}
                />
              </div>
              {filteredCoreAttributes?.map((attribute) => {
                const travellerAttribute = traveller?.companyTravellerAttributes.find(
                  (companyAttribute) => companyAttribute.attributeShortId === attribute.shortId
                );
                return (
                  attribute.isActive && (
                    <ViewCompanyTravellerAttributes
                      key={attribute.shortId}
                      attributeTemplate={attribute}
                      travellerAttribute={travellerAttribute}
                    />
                  )
                );
              })}
            </Stack>
          </Collapse>
        </Grid>
        <Sidebar.Separator />
        <Grid>
          <div>
            <Button
              variant="ghost"
              text={t('journeyAttributes')}
              iconRight={showJourneyAttributes ? <IconDropside /> : <IconDropdown />}
              onClick={() => setShowJourneyAttributes(!showJourneyAttributes)}
            />
          </div>
          <Collapse isCollapsed={showJourneyAttributes} keepCollapsedInDom>
            <Stack space={4}>
              <Text font="regular14" color="textHelper" text={t('fieldsUsedInThisJourney')} />
              {journeyAttributes.length > 0 ? (
                <Stack space={4}>
                  {journeyAttributes?.map((attribute) => {
                    const travellerAttribute = isEdit
                      ? traveller?.journeyAttributes.find(
                          (journeyAttribute) =>
                            journeyAttribute.attributeShortId === attribute.shortId
                        )
                      : undefined;
                    return (
                      attribute.isActive && (
                        <TravellerJourneyAttributeFields
                          key={attribute.shortId}
                          attributeTemplate={attribute}
                          attribute={travellerAttribute}
                          usersData={usersData}
                          onFileMultiRemove={onFileMultiRemove}
                        />
                      )
                    );
                  })}
                </Stack>
              ) : (
                <NoResultCard icon={IconAttributeJourney} text={t('nojourneyAttributes')} />
              )}
            </Stack>
          </Collapse>
        </Grid>
        <Sidebar.Separator />
        {!isEdit && (
          <>
            <Grid>
              <div>
                <Button
                  variant="ghost"
                  text={t('options')}
                  iconRight={showOptions ? <IconDropside /> : <IconDropdown />}
                  onClick={() => setShowOptions(!showOptions)}
                />
              </div>

              <Collapse isCollapsed={showOptions} keepCollapsedInDom>
                <Stack space={4} orientation="horizontal" align="center" justify="space-between">
                  <Stack>
                    <FormLabel text={t('startWhenAdded')} />
                    {startJourney ? (
                      <Text text={t('core.yes')} font="regular14" />
                    ) : (
                      <Text color="purple50" text={t('core.no')} font="regular14" />
                    )}
                  </Stack>
                  <Form.Switch
                    id="startJourney"
                    label={t('startWhenAdded')}
                    labelVisible={false}
                    defaultValue={false}
                  />
                </Stack>
              </Collapse>
            </Grid>
            <Sidebar.Separator />
          </>
        )}
        <Sidebar.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
            <Form.Submit
              text={isEdit ? t('core.save') : t('addToJourney')}
              variant="primary-blue"
              isDisabled={isEdit && !isDirty}
            />
          </Form.Actions>
        </Sidebar.Footer>
      </Stack>
    </Form>
  );
};

type CommonFieldProps = {
  id: keyof Attributes;
  label: string;
  validators?: FormValidatorType[];
  defaultValue?: string;
};

export const TravellerJourneyAttributeFields = ({
  attributeTemplate,
  attribute,
  usersData,
  onFileMultiRemove,
}: {
  attributeTemplate: AttributeType | TravellerAttributeTemplate;
  attribute?: TravellerAttribute;
  usersData?: User[];
  onFileMultiRemove?: (file: string) => void;
}) => {
  const { store } = useFormContext();

  const usersList = usersData?.map((user) => ({ value: user.shortId, label: user.name })) ?? [];

  const listQuery = useList(attributeTemplate.options ?? '');

  const fieldId: keyof Attributes = `${journeyAttributePrefix}${attributeTemplate.shortId}`;

  const commonFieldProps: CommonFieldProps = {
    id: fieldId,
    label: attributeTemplate.title,
    defaultValue: attribute?.value
      ? attributeTemplate.attributeType === 'user'
        ? attribute.value?.shortId
        : attributeTemplate.attributeType === 'single_select'
        ? attribute.value.value
        : attribute.value
      : '',
    validators: attributeTemplate.isRequired ? ['required'] : [],
  };

  switch (attributeTemplate.attributeType) {
    case 'text':
      return <Form.TextField {...commonFieldProps} />;
    case 'paragraph':
      return <Form.Textarea {...commonFieldProps} />;
    case 'date':
      return <Form.DatePicker {...commonFieldProps} placeholder="DD / MM / YYYY" />;
    case 'user':
      return <Form.Select {...commonFieldProps} options={usersList} />;
    case 'phone_number':
      return <Form.PhoneField {...commonFieldProps} />;
    case 'email':
      return (
        <Form.TextField
          {...commonFieldProps}
          validators={[...(commonFieldProps.validators ?? []), 'email']}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <Form.NumberField
          {...commonFieldProps}
          defaultValue={hasNumericValue(attribute?.value) ? attribute?.value : undefined}
          isDecimal={attributeTemplate.attributeType === 'decimal'}
        />
      );
    case 'single_select':
      return (
        <Form.Select
          {...commonFieldProps}
          options={(listQuery.data?.options ?? []).map((option) => ({
            value: option.shortId,
            label: option.label,
          }))}
        />
      );
    case 'multi_select':
      const optionValues = store.useFieldValue(fieldId) as SelectOption[];
      const chosenOptionsLabels = (optionValues ?? []).map((opt) => opt.label);
      const options = (listQuery.data?.options ?? []).map((option) => ({
        value: option.shortId,
        label: option.label,
      }));
      const filteredOptions = options.filter(
        (option) => !chosenOptionsLabels.includes(option.label)
      );
      return (
        <Form.SelectMulti
          defaultValue={attribute?.multiOptionValues}
          label={commonFieldProps.label}
          validators={commonFieldProps.validators}
          options={filteredOptions}
          // @ts-ignore
          id={fieldId}
        />
      );
    case 'file':
      const file = attribute?.files.length
        ? {
            id: attribute?.files[0].id,
            name: attribute?.files[0].name,
            src: attribute?.files[0].src,
          }
        : undefined;
      return (
        <Form.FileField
          // @ts-ignore
          id={fieldId}
          label={commonFieldProps.label}
          validators={commonFieldProps.validators}
          defaultValue={file}
        />
      );
    case 'file_multi':
      const defultFiles = attribute?.files.length
        ? attribute?.files?.map((file) => ({
            id: file.id,
            name: file.name,
            src: file.src,
          }))
        : [];
      return (
        <Form.FileFieldMulti
          // @ts-ignore
          id={fieldId}
          label={commonFieldProps.label}
          defaultValue={defultFiles}
          validators={commonFieldProps.validators}
          onRemove={(file) => {
            onFileMultiRemove?.(file.id);
          }}
        />
      );
    default:
      return null;
  }
};

const AddCompanyTravellerSidebarLocale = {
  en: {
    addToJourney: 'Add to journey',
    add: 'Add',
    createTraveller: 'Create traveller',
    createAndAdd: 'Create and add traveller',
    emptyText: 'All available Travellers added',
    noResults: 'No results based on your search',
    search: 'Search',
    showAlreadyAdded: 'Show already added',
    picture: 'Profile image',
    acceptedFiles: 'Accepted file types: .png and .jpg',
    travellerAttributes: 'Traveller fields',
    journeyAttributes: 'Journey fields',
    nojourneyAttributes: 'There are no journey fields for this journey',
    editTravellerJourney: 'Edit traveller journey fields',
    editTravellerAttributes: 'Edit traveller fields',
    travellerExists: 'Traveller with this email already exists',
    nophone: 'No number added',
    travellerAddedToJourney: 'Traveller added to journey',
    details: 'Details',
    profileFields: 'Profile fields',
    fieldsUsedInThisJourney: 'Fields used in this journey',
    options: 'Options',
    startWhenAdded: 'Start journey when added',
  },
};

const Grid = styled.div(() => ({
  display: 'grid',
  gridTemplateColumns: '2fr 4fr',
  gap: 0,
}));
